import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { getAuth, signOut, updateEmail } from "firebase/auth";
import { updateDoc } from "firebase/firestore";
import UsuarioDb from "../../../../db/usuarioDb";
import {
  IconCheck,
  IconLogout,
  IconMoon,
  IconSunHigh,
} from "@tabler/icons-react";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import {
  atualizarPerfil,
  useUsuario,
} from "../../../../providers/usuarioProvider";
import { Perfil } from "../../../../models/enum";
import UploadAvatar from "../../../util/ui/uploadAvatar";
import { IconPencil } from "@tabler/icons-react";
import FelixAvatar from "../../../util/ui/felixAvatar";
import {
  useThemeMode,
  useToggleThemeMode,
} from "../../../../providers/themeModeContext";

export default function AbaPerfil() {
  const { usuario, claims } = useUsuario();
  const [editar, setEditar] = useState(false);
  const currentUser = getAuth().currentUser;
  const [nome, setNome] = useState(usuario?.nome);
  const [email, setEmail] = useState(usuario?.email);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const themeMode = useThemeMode();
  const toggleThemeMode = useToggleThemeMode();

  const saveImage = async (file: HTMLCanvasElement) => {
    if (!!currentUser) {
      setUploading(true);
      const imagemRef = ref(
        getStorage(),
        `/usuarios/${currentUser.uid}/perfil.jpeg`,
      );

      await new Promise<Blob>((resolve, reject) => {
        file.toBlob((blob) => {
          if (!!blob) resolve(blob);
          else reject();
        });
      })
        .then((imageBlob) =>
          uploadBytes(imagemRef, imageBlob, {
            contentType: "image/jpeg",
          }),
        )
        .then((u) => getDownloadURL(u.ref))
        .then((url) => {
          atualizarPerfil(currentUser, { ...currentUser, photoURL: url });
          return url;
        })
        .then((url) =>
          updateDoc(new UsuarioDb().doc({ usuarioId: currentUser.uid }), {
            fotoDePerfil: url,
          }),
        )
        .then(() => setUploading(false));
    }
  };

  useEffect(() => {
    if (!!usuario) {
      setNome(usuario?.nome);
      setEmail(usuario?.email);
    }
  }, [usuario]);

  return (
    <Container disableGutters>
      <Box display="flex" flexDirection="row" alignItems={"center"}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems={"center"}
          paddingRight={2}
        >
          <FelixAvatar
            nome={nome ?? ""}
            size={80}
            perfil={claims?.perfil ?? Perfil.INVESTIDOR}
            fotoDePerfil={usuario?.fotoDePerfil ?? currentUser?.photoURL ?? ""}
          />
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h3" color="text.primary">
            {currentUser?.displayName}
          </Typography>
          <Typography variant="dataMono" fontSize={13} color="text.secondary">
            {currentUser?.email}
          </Typography>
          <Box paddingTop={1} />
          {/* Botão de texto, não link em rótulo mono: "Editar" é uma AÇÃO, e
              como caption mono minúsculo a afordância de clique sumia. */}
          <Button
            variant="text"
            size="small"
            startIcon={<IconPencil size={16} />}
            onClick={() => setEditar(true)}
            sx={{ alignSelf: "flex-start", ml: -1 }}
          >
            Editar
          </Button>
        </Box>
        <Box
          flexGrow={1}
          alignSelf="flex-start"
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          gap={1}
        >
          <Button
            sx={{ minWidth: 100 }}
            onClick={() =>
              toggleThemeMode(themeMode === "light" ? "dark" : "light")
            }
            variant="outlined"
            startIcon={
              themeMode === "dark" ? (
                <IconSunHigh size={18} />
              ) : (
                <IconMoon size={18} />
              )
            }
          >
            Tema
          </Button>
          <Button
            sx={{ minWidth: 100 }}
            variant="contained"
            color="error"
            startIcon={<IconLogout size={18} />}
            onClick={() => signOut(getAuth())}
          >
            Sair
          </Button>
        </Box>
      </Box>
      <Dialog
        maxWidth="xs"
        onClose={() => {
          setError("");
          setEditar(false);
        }}
        aria-labelledby="simple-dialog-title"
        open={editar}
      >
        <DialogTitle sx={{ typography: "h3" }}>Editar perfil</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="stretch">
            <Box alignSelf="center">
              <UploadAvatar onSave={saveImage}>
                <FelixAvatar
                  nome={nome ?? ""}
                  size={100}
                  perfil={claims?.perfil ?? Perfil.INVESTIDOR}
                  fotoDePerfil={
                    !uploading
                      ? (usuario?.fotoDePerfil ?? currentUser?.photoURL ?? "")
                      : undefined
                  }
                />
              </UploadAvatar>
            </Box>
            <Box paddingY={2}>
              <Divider variant="fullWidth" />
            </Box>
            <FormControl
              variant="outlined"
              fullWidth
              margin="normal"
              color={currentUser?.displayName === nome ? "success" : undefined}
            >
              <Typography
                variant="labelMono"
                component="label"
                htmlFor="nome-input"
                color="text.secondary"
                mb={0.75}
              >
                Nome
              </Typography>
              <OutlinedInput
                id="nome-input"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={async () =>
                        currentUser &&
                        atualizarPerfil(currentUser, {
                          displayName: nome,
                        }).then(() => getAuth().currentUser?.reload())
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      size="large"
                      // accent como TINTA (accentInk) — primary.main reprova
                      // em contraste sobre superfície escura.
                      sx={(theme) => ({
                        color:
                          currentUser?.displayName === nome
                            ? theme.palette.success.main
                            : theme.palette.accentInk,
                      })}
                    >
                      <IconCheck strokeWidth={3} />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
            <Box paddingY={2}>
              <Divider variant="fullWidth" />
            </Box>
            <FormControl
              variant="outlined"
              fullWidth
              margin="normal"
              color={currentUser?.email === email ? "success" : undefined}
            >
              <Typography
                variant="labelMono"
                component="label"
                htmlFor="email-input"
                color="text.secondary"
                mb={0.75}
              >
                Email
              </Typography>
              <OutlinedInput
                id="email-input"
                error={!!error}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={async () =>
                        currentUser &&
                        email &&
                        updateEmail(currentUser, email)
                          .then(() =>
                            updateDoc(
                              new UsuarioDb().doc({
                                usuarioId: currentUser.uid,
                              }),
                              { email: email },
                            ),
                          )
                          .catch((error) => {
                            switch (error.code) {
                              case "auth/requires-recent-login":
                                setError(
                                  "Faz muito tempo que você fez login. Para sua segurança faça um novo login e tente novamente.",
                                );
                                break;
                              case "auth/email-already-in-use":
                                setError("Esse email já está em uso.");
                                break;

                              default:
                                setError(
                                  "Não é possível realizar essa operação.",
                                );
                                break;
                            }
                          })
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      size="large"
                      sx={(theme) => ({
                        color:
                          currentUser?.email === email
                            ? theme.palette.success.main
                            : theme.palette.accentInk,
                      })}
                    >
                      <IconCheck strokeWidth={3} />
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText id="component-error-text">
                {error
                  ? error
                  : "Certifique-se que o novo email existe antes de realizar essa operação."}
              </FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            sx={{ color: "accentInk" }}
            onClick={() => setEditar(false)}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
